import React, { SetStateAction, useEffect, useState } from "react";

import {
	FrontendClient,
	FrontendCompanyTestCategory,
	FrontendCompanyWithAdditionalInfomation,
	FrontendOrganization,
	FrontendOrganizationLicense,
	FrontendSample,
	FrontendSampleSpecification,
	FrontendSampleSpecificationDirectory,
	FrontendUserInfo,
} from "node-library/dist/portal";
import { useTranslation } from "react-i18next";

import { Severity, SystemRole } from "node-library/dist/constants";
import { formatCustomDate } from "node-library/dist/helpers/date_helper";

import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import { Card } from "@mui/material";
import { ButtonTextLoadingAnimation } from "../../../common-components/animation/ButtonLoadingAnimation";
import {
	BlackButton,
	CustomIconButton,
	GreenButton,
} from "../../../common-components/button";
import { RedErrorIcon } from "../../../common-components/toast/errorIcons";
import { useSnackbar } from "../../../common-components/toast/showToastMessage";
import { sendGETRequest } from "../../../requestHelper/sendRequest";

import AddIcon from "@mui/icons-material/Add";
import NewOrEditOrder from "../newOrEditOrderPage";
import ClientInfoSection from "./clientInfoSection";
import SelectClientModal from "./modal/selectClientModal";
import OrgDetailedCategoryInfoContent from "./orgDetailedCategoryInfoContent";
import {
	hasPermission,
	PermissionMap,
	Permissions,
} from "node-library/dist/permissions";

interface Props {
	company: FrontendCompanyWithAdditionalInfomation;
	setCompany: React.Dispatch<
		React.SetStateAction<FrontendCompanyWithAdditionalInfomation | undefined>
	>;
	user: FrontendUserInfo | undefined;
	selectedOrganization: FrontendOrganization;
	goBackToViewOrganizationPage: (refresh: boolean) => void;
	adminInAction: boolean;
	setAddEditOrgModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
	setUnsavedChanges: React.Dispatch<SetStateAction<boolean>>;
	gotoViewOrderPage: () => void;
}

export default function OrgDetailsContent(props: Props) {
	const { t: translation } = useTranslation();
	const { openSnackbar } = useSnackbar();

	const [loading, setLoading] = useState<boolean>(false);
	const [clients, setClients] = useState<Array<FrontendClient>>([]);
	const [licenses, setLicenses] = useState<Array<FrontendOrganizationLicense>>(
		[],
	);
	const [samples, setSamples] = useState<Array<FrontendSample>>([]);

	const [orgSpecifications, setOrgSpecifications] = useState<
		Array<FrontendSampleSpecification>
	>([]);

	const [specDirectories, setSpecDirectories] = useState<
		Array<FrontendSampleSpecificationDirectory>
	>([]);

	const [selectedClient, setSelectedClient] = useState<
		FrontendClient | undefined
	>();
	const [selectClientModalOpen, setSelectClientModalOpen] =
		useState<boolean>(false);
	const [currentPageIndex, setCurrentPageIndex] = useState<number>(0);

	const [orgDetailsSelectedCategory, setOrgDetailsSelectedCategory] = useState<
		FrontendCompanyTestCategory | undefined
	>(
		props.company.companyTestCategories.length > 0
			? props.company.companyTestCategories[0]
			: undefined,
	);

	const [newOrderSelectedCategory, setNewOrderSelectedCategory] = useState<
		FrontendCompanyTestCategory | undefined
	>(
		props.company.companyTestCategories.length > 0
			? props.company.companyTestCategories[0]
			: undefined,
	);

	const {
		goBackToViewOrganizationPage,
		selectedOrganization,
		adminInAction,

		setAddEditOrgModalOpen,
	} = props;

	const doesUserHaveCreateOrderAccess: boolean = props.user
		? hasPermission(
				props.user?.role?.permissions as PermissionMap,
				Permissions.orders.create,
			)
		: false;
	// const doesUserHaveEditPermission: boolean =
	// 	doesUserHaveAdminAccess ||
	// 	(props.user?.role === SystemRole.SALES_REP &&
	// 		selectedOrganization.salesUserId === props.user?.userId);

	async function getOrganizationDetailedInfo() {
		setLoading(true);
		try {
			const response = await sendGETRequest(
				"/client-orgs/get-organization-detailed-info",
				{
					organizationId: selectedOrganization.organizationId,
					testCategoryId: orgDetailsSelectedCategory?.testCategoryId,
				},
			);

			const clients: Array<FrontendClient> = response?.data?.clients;
			setClients(clients);

			const licenses: Array<FrontendOrganizationLicense> =
				response?.data?.licenses;
			setLicenses(licenses);

			const samples: Array<FrontendSample> = response?.data?.samples;
			setSamples(samples);

			const specifications: Array<FrontendSampleSpecification> =
				response?.data?.specifications;
			setOrgSpecifications(specifications);

			const specDirectories: Array<FrontendSampleSpecificationDirectory> =
				response?.data?.specDirectories;
			setSpecDirectories(specDirectories);
		} catch (error: any) {
			console.error(error);
			openSnackbar(
				translation("SystemError"),
				Severity.ERROR,
				translation("SystemErrorMsg"),
				<RedErrorIcon />,
			);
		} finally {
			setLoading(false);
		}
	}

	useEffect(() => {
		getOrganizationDetailedInfo();
	}, [orgDetailsSelectedCategory]);

	let actionOptions: Array<{
		children: React.ReactNode;
	}> = [
		{
			children: (
				<>
					<BlackButton
						key={`edit-client-info`}
						id={"edit-client-info"}
						onClick={() => {
							setAddEditOrgModalOpen(true);
						}}
						disabled={adminInAction}
					>
						{adminInAction ? (
							<ButtonTextLoadingAnimation
								text={translation("Loading")}
							></ButtonTextLoadingAnimation>
						) : (
							translation("EditOrganization")
						)}
					</BlackButton>
					{doesUserHaveCreateOrderAccess && (
						<GreenButton
							key={`client-new-order-button`}
							id={"client-new-order-button"}
							onClick={() => {
								setSelectClientModalOpen(true);
							}}
							disabled={adminInAction}
						>
							{adminInAction ? (
								<ButtonTextLoadingAnimation
									text={translation("Loading")}
								></ButtonTextLoadingAnimation>
							) : (
								<div
									style={{
										height: "100%",
										display: "flex",
										justifyContent: "center",
										alignItems: "center",
									}}
									className="typography-body-01"
								>
									<AddIcon></AddIcon>
									{translation("NewOrder")}
								</div>
							)}
						</GreenButton>
					)}
				</>
			),
		},
	];
	const actionRow: React.ReactNode = (
		<div
			style={{ display: "flex", flexWrap: "wrap", gap: "10px", float: "right" }}
		>
			{actionOptions.map((option) => {
				return option.children;
			})}
		</div>
	);

	const userHeaderRow: React.ReactNode = (
		<div
			style={{
				display: "flex",
				justifyContent: "space-between",
				alignItems: "center",
				gap: "5px",
			}}
		>
			{/* <div
					className="typography-caption sample-status-box"
					style={{
						backgroundColor: statusColor.backgroundColor,
						border: `1px solid ${statusColor.borderColor}`,
						color: statusColor.textColor,
					}}
				>
					{statusString}
				</div> */}
		</div>
	);
	const userBody: React.ReactNode = (
		<div
			style={{
				marginTop: "10px",
				display: "flex",
				flexDirection: "column",
				gap: "5px",
			}}
		>
			<div className="order-cell-body-row typography-body-02">
				<div className="sample-info-row-description">
					{translation("AssociationId")}
				</div>
				<div className="overflow-ellipsis-text" style={{ maxWidth: "50%" }}>
					{selectedOrganization.associationId}
				</div>
			</div>
			<div className="order-cell-body-row typography-body-02">
				<div className="sample-info-row-description">
					{translation("CompanyName")}
				</div>
				<div className="overflow-ellipsis-text" style={{ maxWidth: "50%" }}>
					{selectedOrganization.clientCompanyName}
				</div>
			</div>
			<div className="order-cell-body-row typography-body-02">
				<div className="sample-info-row-description">
					{translation("Address")}
				</div>
				<div
					className="overflow-ellipsis-text"
					style={{ maxWidth: "50%", textAlign: "right" }}
				>
					<div>{`${selectedOrganization.address}, `}</div>
					<div>{`${selectedOrganization.city}, ${selectedOrganization.province}, ${selectedOrganization.country} `}</div>
					<div>{selectedOrganization.postalCode}</div>
				</div>
			</div>

			<div className="order-cell-body-row typography-body-02">
				<div className="sample-info-row-description">
					{translation("SalesRep")}
				</div>
				<div className="overflow-ellipsis-text" style={{ maxWidth: "50%" }}>
					{selectedOrganization.salesFullName || translation("None")}
				</div>
			</div>

			<div className="order-cell-body-row typography-body-02">
				<div className="sample-info-row-description">
					{translation("RegistrationTime")}
				</div>
				<div className="overflow-ellipsis-text" style={{ maxWidth: "50%" }}>
					{formatCustomDate(selectedOrganization.inserted as Date)}
				</div>
			</div>
		</div>
	);
	let currentComponent: JSX.Element = <></>;
	switch (currentPageIndex) {
		case 0:
			currentComponent = (
				<div>
					<CustomIconButton
						id="register-back-button"
						onClick={() => goBackToViewOrganizationPage(false)}
						icon={<KeyboardArrowLeftIcon />}
					></CustomIconButton>
					{actionRow}
					<Card
						className="common-card card-border-radius-14p card-medium-padding"
						key={selectedOrganization.organizationId}
					>
						{userHeaderRow}
						{userBody}
					</Card>
					<ClientInfoSection
						clients={clients}
						setClients={setClients}
						organization={selectedOrganization}
					></ClientInfoSection>

					<OrgDetailedCategoryInfoContent
						setCompany={props.setCompany}
						licenses={licenses}
						setLicenses={setLicenses}
						selectedOrganization={selectedOrganization}
						samples={samples}
						setSamples={setSamples}
						orgSpecifications={orgSpecifications}
						setOrgSpecifications={setOrgSpecifications}
						company={props.company}
						user={props.user}
						orgDetailsSelectedCategory={
							orgDetailsSelectedCategory as FrontendCompanyTestCategory
						}
						setOrgDetailsSelectedCategory={setOrgDetailsSelectedCategory}
						specDirectories={specDirectories}
						setSpecDirectories={setSpecDirectories}
						reloadOrganizationDetailedInfo={getOrganizationDetailedInfo}
						loading={loading}
					></OrgDetailedCategoryInfoContent>
				</div>
			);
			break;
		case 1:
			currentComponent = (
				<NewOrEditOrder
					company={props.company}
					setCompany={props.setCompany}
					user={props.user}
					orderTestCategory={
						newOrderSelectedCategory as FrontendCompanyTestCategory
					}
					client={selectedClient as FrontendClient}
					selectedOrganization={selectedOrganization as FrontendOrganization}
					clientLicenses={licenses}
					gotoViewOrderPage={props.gotoViewOrderPage}
					organizationSamples={samples}
					organizationSpecifications={orgSpecifications}
					setUnsavedChanges={props.setUnsavedChanges}
					goBackToPreviousPage={() => {
						setSelectedClient(undefined);
						setCurrentPageIndex(0);
					}}
					orgInfoLoaded={
						orgDetailsSelectedCategory?.testCategoryId ===
						newOrderSelectedCategory?.testCategoryId
					}
					organizationSpecDirectories={specDirectories}
				></NewOrEditOrder>
			);
	}

	return (
		<>
			{currentComponent}
			{
				<SelectClientModal
					company={props.company}
					setNewOrderSelectedCategory={setNewOrderSelectedCategory}
					open={selectClientModalOpen}
					setOpen={setSelectClientModalOpen}
					clients={clients}
					selectClient={(client: FrontendClient) => {
						setSelectedClient(client);
						setSelectClientModalOpen(false);
						setCurrentPageIndex(1);
					}}
				/>
			}
		</>
	);
}
